import { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { usePortal } from "../../classes/contexts/PortalContext";
import { SnackbarPosition } from "../../classes/enums/SnackbarPositionEnum";
import { SnackbarType } from "../../classes/enums/SnackbarTypeEnum";
import { getSnackbarIcon } from "../../config/SnackbarIconConfig";
import "./Snackbar.css";

const SNACKBAR_TITLES = {
  [SnackbarType.SUCCESS]: "Success",
  [SnackbarType.INFO]: "Info",
  [SnackbarType.WARNING]: "Warning",
  [SnackbarType.ERROR]: "Error",
  [SnackbarType.LOADING]: "Processing",
};

const LoadingDots = () => (
  <span className="snackbar-loading-dots">
    <span />
    <span />
    <span />
  </span>
);

const Snackbar = ({
  type = SnackbarType.INFO,
  message,
  title,
  duration = 4000,
  position = SnackbarPosition.BOTTOM,
  onClose,
  usePortal: shouldUsePortal = true,
}) => {
  const portalRoot = usePortal();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  if (!message) return null;

  const resolvedTitle = title || SNACKBAR_TITLES[type];

  const snackbarContent = (
    <div
      className={`snackbar-container snackbar-${type} snackbar-${position} ${
        isVisible && !isExiting ? "snackbar-visible" : ""
      } ${isExiting ? "snackbar-exiting" : ""}`}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="snackbar-content">
        <div className="snackbar-icon" aria-hidden="true">
          {getSnackbarIcon(type)}
        </div>
        <div className="snackbar-text">
          {resolvedTitle && (
            <div className="snackbar-title">{resolvedTitle}</div>
          )}
          {message && (
            <div className="snackbar-message">
              {message}
              {type === SnackbarType.LOADING && <LoadingDots />}
            </div>
          )}
        </div>
        <button
          className="snackbar-close-button"
          onClick={handleClose}
          aria-label={`Close ${resolvedTitle || "notification"}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  if (!shouldUsePortal) {
    return snackbarContent;
  }

  if (!portalRoot) return null;

  return ReactDOM.createPortal(snackbarContent, portalRoot);
};

export default Snackbar;
